import {useCallback, useEffect, useRef, useState} from "react";
import {NEURON_EVENTS} from "../config/eventTypes";
import {trackNeuronEvent} from "../utils/trackNeuronEvent";

const INTERSECTION_THRESHOLD = 0.5;
const ROOT_MARGIN = "-18% -6% -42% -6%";
const VIEW_INTERCEPTOR_DELAY_MS = 2000;
const PENDING_HIGHLIGHT_CLASS = "view-interceptor-pending";

export const useViewInterceptor = ({userId}) => {
  const [firedCount, setFiredCount] = useState(0);
  const [rootElement, setRootElement] = useState(null);
  const firedItemIdsRef = useRef(new Set());
  const observerRef = useRef(null);
  const targetMapRef = useRef(new Map());
  const pendingTimeoutsRef = useRef(new Map());
  const visibleItemsRef = useRef(new Set());
  const userIdRef = useRef(userId);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const setRootRef = useCallback((node) => {
    setRootElement(node || null);
  }, []);

  const clearPending = useCallback((itemId) => {
    const pending = pendingTimeoutsRef.current.get(itemId);
    if (!pending) return;
    clearTimeout(pending.timeoutId);
    pending.target?.classList.remove(PENDING_HIGHLIGHT_CLASS);
    pendingTimeoutsRef.current.delete(itemId);
  }, []);

  const logViewEvent = useCallback(async (itemId, target) => {
    const currentUserId = userIdRef.current;
    if (!currentUserId) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[ViewInterceptor] missing userId; skipping event",
          {itemId}
        );
      }
      return;
    }

    clearPending(itemId);
    firedItemIdsRef.current.add(itemId);
    observerRef.current?.unobserve(target);
    setFiredCount((prev) => prev + 1);

    if (process.env.NODE_ENV !== "production") {
      console.info("[ViewInterceptor] fired", {itemId});
    }

    try {
      await trackNeuronEvent({
        eventId: NEURON_EVENTS.impression,
        itemId,
        userId: currentUserId,
        metadata: {
          source: "view_interceptor",
        },
      });
      window.dispatchEvent(
        new CustomEvent("neuronEventLogged", {
          detail: {
            message: "View interceptor event logged.",
            status: "success",
          },
        })
      );
    } catch (error) {
      console.error("💥 Error sending view interceptor event:", error);
      window.dispatchEvent(
        new CustomEvent("neuronEventLogged", {
          detail: {
            message: "Failed to log view interceptor event.",
            status: "error",
          },
        })
      );
    }
  }, [clearPending]);

  const handleIntersect = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        const itemId = entry.target.dataset.neuronItemId;
        if (!itemId) return;
        if (!entry.isIntersecting) {
          visibleItemsRef.current.delete(itemId);
          clearPending(itemId);
          return;
        }
        if (entry.intersectionRatio < INTERSECTION_THRESHOLD) {
          visibleItemsRef.current.delete(itemId);
          clearPending(itemId);
          return;
        }
        visibleItemsRef.current.add(itemId);
        if (firedItemIdsRef.current.has(itemId)) return;
        if (pendingTimeoutsRef.current.has(itemId)) return;

        entry.target.classList.add(PENDING_HIGHLIGHT_CLASS);
        const timeoutId = window.setTimeout(() => {
          if (!visibleItemsRef.current.has(itemId)) {
            clearPending(itemId);
            return;
          }
          clearPending(itemId);
          void logViewEvent(itemId, entry.target);
        }, VIEW_INTERCEPTOR_DELAY_MS);

        pendingTimeoutsRef.current.set(itemId, {
          timeoutId,
          target: entry.target,
        });
      });
    },
    [clearPending, logViewEvent]
  );

  useEffect(() => {
    if (!rootElement) return undefined;

    const observer = new IntersectionObserver(handleIntersect, {
      root: rootElement,
      rootMargin: ROOT_MARGIN,
      threshold: INTERSECTION_THRESHOLD,
    });

    observerRef.current = observer;

    targetMapRef.current.forEach((node, itemId) => {
      if (!node) return;
      if (firedItemIdsRef.current.has(itemId)) return;
      observer.observe(node);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [handleIntersect, rootElement]);

  useEffect(() => {
    const pendingTimeouts = pendingTimeoutsRef.current;
    const visibleItems = visibleItemsRef.current;

    return () => {
      pendingTimeouts.forEach(({timeoutId, target}) => {
        clearTimeout(timeoutId);
        target?.classList.remove(PENDING_HIGHLIGHT_CLASS);
      });
      pendingTimeouts.clear();
      visibleItems.clear();
    };
  }, []);

  const getItemRef = useCallback(
    (itemId) => {
      if (!itemId) {
        return null;
      }

      return (node) => {
        const previousNode = targetMapRef.current.get(itemId);

        if (previousNode && observerRef.current) {
          observerRef.current.unobserve(previousNode);
        }

        if (!node) {
          clearPending(itemId);
          visibleItemsRef.current.delete(itemId);
          targetMapRef.current.delete(itemId);
          return;
        }

        node.dataset.neuronItemId = String(itemId);
        targetMapRef.current.set(itemId, node);

        if (
          observerRef.current &&
          !firedItemIdsRef.current.has(itemId)
        ) {
          observerRef.current.observe(node);
        }
      };
    },
    [clearPending]
  );

  return {
    firedCount,
    getItemRef,
    rootRef: setRootRef,
  };
};
